import { zodResolver } from '@hookform/resolvers/zod';
import {
  type ChangePinInput,
  changePinInput,
  type DeskCreatedDto,
  type DeskCreateInput,
  deskCreateInput,
  formatMoney,
  ROLE_META,
} from '@hudhuria/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactNode, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { clearLocalData } from '~/api/client';
import { useTodayView } from '~/api/derived';
import { usePendingCount } from '~/api/pending';
import { qk, teamQuery, useCan, useCenter, useMe } from '~/api/queries';
import {
  BarsIcon,
  ChevronRight,
  CloudIcon,
  DeskIcon,
  FaceIdIcon,
  FingerprintIcon,
  IdCardIcon,
  KeyIcon,
  LockIcon,
  SheetIcon,
  ShieldTeamIcon,
} from '~/components/icons';
import {
  Avatar,
  BottomSheet,
  FieldError,
  PrimaryButton,
  SecondaryButton,
  SectionLabel,
  TextField,
  Toggle,
} from '~/components/ui';
import { ApiError, api, ok } from '~/lib/api';
import {
  type Biometry,
  getBiometry,
  setBiometricEnabled,
  useBiometricEnabled,
  verifyBiometric,
} from '~/lib/biometric';
import { signInPath } from '~/lib/entry';
import { toast } from '~/lib/toast';
import { setForcedOffline, useForcedOffline, useOnline } from '~/offline/online';
import { session, useSession } from '~/session/session';

export const Route = createFileRoute('/_app/_tabs/me')({ component: Me });

function Me() {
  const navigate = useNavigate();
  const me = useMe().data;
  const center = useCenter();
  const { centerId, desk } = useSession();
  const biometricOn = useBiometricEnabled();
  const [biometry, setBiometry] = useState<Biometry | null>(null);
  const [sheet, setSheet] = useState<'pin' | 'deskOn' | 'deskOff' | null>(null);
  const view = useTodayView();
  const pending = usePendingCount();
  const online = useOnline();
  const forced = useForcedOffline();
  const canStaff = useCan('staff');
  const canExport = useCan('export');
  const canCards = useCan('idcards');
  const team = useQuery({ ...teamQuery, enabled: canStaff });
  const [confirmOut, setConfirmOut] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  useEffect(() => {
    void getBiometry().then(setBiometry);
  }, []);
  if (!me) return null;

  const meta = ROLE_META[me.staff.role];
  const roleLine =
    me.staff.role === 'super'
      ? `Super admin · all ${me.centers.length} centers`
      : `${meta.label} · ${center?.name ?? ''}`;
  const switchable = me.centers.length > 1;

  const signOut = async () => {
    setSigningOut(true);
    try {
      await api.auth.logout.$post();
    } catch {
      /* offline: the token simply expires */
    }
    await clearLocalData();
    setBiometricEnabled(false);
    session.signOut();
    void navigate({ to: signInPath(), replace: true });
  };

  return (
    <div className="flex-1 animate-fade-in-fast px-5 pt-safe-6 pb-nav scroll-y">
      <div className="font-display text-[28px] font-bold tracking-[-0.02em]">Me</div>
      <div className="mt-4 flex items-center gap-[13px] rounded-[18px] bg-fill-soft p-[15px]">
        <Avatar name={me.staff.name} size={50} radius="full" color="#0369a1" fontSize={16} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-[7px]">
            <div className="text-[15.5px] font-semibold">{me.staff.name}</div>
            <span
              className="rounded-[20px] px-2 py-[3px] text-[10.5px] font-bold"
              style={{ background: meta.bg, color: meta.fg }}
            >
              {meta.label}
            </span>
          </div>
          <div className="mt-px truncate text-[12.5px] text-slate-500">{roleLine}</div>
        </div>
        {desk && (
          <button
            type="button"
            onClick={() => {
              // queued writes replay under whoever is signed in, so let them finish first
              if (pending && online) toast(`Wait a moment — ${pending} still syncing`);
              else void navigate({ to: '/desk', search: { switch: true } });
            }}
            className="shrink-0 cursor-pointer rounded-[10px] border-none bg-brand-50 px-[11px] py-2 text-[12.5px] font-bold text-brand"
          >
            Switch staff
          </button>
        )}
      </div>

      <SectionLabel className="mx-0.5 mt-[22px] mb-[9px] text-[11.5px] tracking-[0.07em]">
        Center
      </SectionLabel>
      {switchable ? (
        <div className="flex flex-col overflow-hidden rounded-2xl border border-line">
          {me.centers.map((c) => {
            const on = c.id === centerId;
            return (
              <button
                type="button"
                key={c.id}
                onClick={() => {
                  if (on) return;
                  session.setCenter(c.id);
                  toast(`Switched to ${c.name}`);
                }}
                className="flex cursor-pointer items-center gap-[11px] border-0 border-b border-solid border-line-faint px-[14px] py-3 text-left last:border-b-0"
                style={{ background: on ? '#f0f8fd' : '#fff' }}
              >
                <div
                  className="w-[30px] font-display text-[12px] font-bold"
                  style={{ color: on ? '#0369a1' : '#94a3b8' }}
                >
                  {c.code}
                </div>
                <div className="flex-1 text-[14px]" style={{ fontWeight: on ? 600 : 500 }}>
                  {c.name}
                </div>
                <div className="text-[14px] font-bold text-brand">{on ? '✓' : ''}</div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="flex items-center gap-[11px] rounded-2xl border border-line bg-brand-25 px-[14px] py-[13px]">
          <div className="w-[30px] font-display text-[12px] font-bold text-brand">{center?.code}</div>
          <div className="flex-1">
            <div className="text-[14px] font-semibold">{center?.name}</div>
            <div className="mt-px text-[11.5px] text-slate-400">
              Assigned by {me.assignedBy ?? 'your admin'}
            </div>
          </div>
          <LockIcon />
        </div>
      )}

      <SectionLabel className="mx-0.5 mt-[22px] mb-[9px] text-[11.5px] tracking-[0.07em]">Tools</SectionLabel>
      <div className="flex flex-col overflow-hidden rounded-2xl border border-line">
        {canStaff && (
          <ToolRow
            icon={<ShieldTeamIcon />}
            iconBg="#f5f3ff"
            label="Team & roles"
            detail={team.data ? `${team.data.length} staff` : ''}
            onClick={() => void navigate({ to: '/team' })}
          />
        )}
        {canExport && (
          <ToolRow
            icon={<SheetIcon />}
            iconBg="#ecfdf3"
            label="Export report (Excel)"
            detail=".xlsx"
            onClick={() => void navigate({ to: '/export' })}
          />
        )}
        {canCards && (
          <ToolRow
            icon={<IdCardIcon />}
            iconBg="#e6f4fc"
            label="Print ID cards"
            detail={String(center?.enrolledLive ?? '')}
            onClick={() => void navigate({ to: '/id-cards' })}
          />
        )}
        <ToolRow
          icon={<BarsIcon />}
          iconBg="#e6f4fc"
          label="Today's report"
          detail={`${view.pct}%${view.collectedMinor != null ? ` · ${formatMoney(view.collectedMinor, view.currency)}` : ''}`}
          onClick={() => void navigate({ to: '/timeline' })}
        />
        <div className="flex items-center gap-3 px-[14px] py-[13px]">
          <ToolIcon bg="#e6f4fc">
            <CloudIcon />
          </ToolIcon>
          <div className="flex-1">
            <div className="text-[14px] font-medium">Offline mode</div>
            {pending > 0 && <div className="text-[11.5px] text-slate-400">{pending} waiting to sync</div>}
          </div>
          <Toggle
            on={forced}
            label="Offline mode"
            onChange={(v) => {
              setForcedOffline(v);
              toast(
                v
                  ? 'Offline · check-ins will queue'
                  : pending
                    ? 'Back online · syncing'
                    : 'Back online · synced',
              );
            }}
          />
        </div>
      </div>

      <SectionLabel className="mx-0.5 mt-[22px] mb-[9px] text-[11.5px] tracking-[0.07em]">
        Signing in
      </SectionLabel>
      <div className="flex flex-col overflow-hidden rounded-2xl border border-line">
        {canStaff && (
          <div className="flex items-center gap-3 border-0 border-b border-solid border-line-faint px-[14px] py-[13px]">
            <ToolIcon bg="#e6f4fc">
              <DeskIcon className="text-brand" />
            </ToolIcon>
            <div className="min-w-0 flex-1">
              <div className="text-[14px] font-medium">Front-desk mode</div>
              <div className="text-[11.5px] text-slate-400">
                {desk ? `On · ${desk.name} · ${desk.centerName}` : 'Staff pick their name + PIN here'}
              </div>
            </div>
            <Toggle
              on={!!desk}
              label="Front-desk mode"
              onChange={(v) => setSheet(v ? 'deskOn' : 'deskOff')}
            />
          </div>
        )}
        {biometry && !desk && (
          <div className="flex items-center gap-3 border-0 border-b border-solid border-line-faint px-[14px] py-[13px]">
            <ToolIcon bg="#ecfdf3">
              {biometry.face ? (
                <FaceIdIcon className="text-emerald-700" />
              ) : (
                <FingerprintIcon className="text-emerald-700" />
              )}
            </ToolIcon>
            <div className="min-w-0 flex-1">
              <div className="text-[14px] font-medium">Unlock with {biometry.label}</div>
              <div className="text-[11.5px] text-slate-400">Asked when Hudhuria opens</div>
            </div>
            <Toggle
              on={biometricOn}
              label={`Unlock with ${biometry.label}`}
              onChange={async (v) => {
                if (!v) {
                  setBiometricEnabled(false);
                  return;
                }
                if (await verifyBiometric(`Turn on ${biometry.label} for Hudhuria`)) {
                  setBiometricEnabled(true);
                  toast(`${biometry.label} unlock is on`);
                }
              }}
            />
          </div>
        )}
        <ToolRow
          icon={<KeyIcon size={17} className="text-amber-700" />}
          iconBg="#fef3c7"
          label="Change PIN"
          detail=""
          onClick={() => setSheet('pin')}
        />
      </div>

      <button
        type="button"
        onClick={() => (pending ? setConfirmOut(true) : void signOut())}
        disabled={signingOut}
        className="mt-5 w-full cursor-pointer border-none bg-transparent p-[10px] text-center text-[14px] font-semibold text-red-500"
      >
        {signingOut ? 'Signing out…' : 'Sign out'}
      </button>
      <div className="text-center text-[11.5px] text-slate-400">
        {me.institution.name} · {me.staff.code}
      </div>

      {sheet === 'pin' && <ChangePinSheet onClose={() => setSheet(null)} />}
      {sheet === 'deskOn' && center && <DeskOnSheet center={center} onClose={() => setSheet(null)} />}
      {sheet === 'deskOff' && desk && <DeskOffSheet onClose={() => setSheet(null)} />}

      {confirmOut && (
        <BottomSheet onClose={() => setConfirmOut(false)}>
          <div className="font-display text-[20px] font-bold">Sign out with unsynced work?</div>
          <div className="mt-2 text-[14px] leading-normal text-slate-500">
            {pending} {pending === 1 ? 'change hasn’t' : 'changes haven’t'} reached the server yet. Signing
            out now deletes {pending === 1 ? 'it' : 'them'} from this device.
            {!online && ' Turn off offline mode or find a signal to sync first.'}
          </div>
          <div className="mt-5 flex flex-col gap-[10px]">
            <PrimaryButton onClick={() => setConfirmOut(false)}>Stay signed in</PrimaryButton>
            <SecondaryButton className="text-red-500" onClick={() => void signOut()}>
              Sign out anyway
            </SecondaryButton>
          </div>
        </BottomSheet>
      )}
    </div>
  );
}

function ToolIcon({ bg, children }: { bg: string; children: ReactNode }) {
  return (
    <div
      className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[10px]"
      style={{ background: bg }}
    >
      {children}
    </div>
  );
}

function ToolRow({
  icon,
  iconBg,
  label,
  detail,
  onClick,
}: {
  icon: ReactNode;
  iconBg: string;
  label: string;
  detail: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full cursor-pointer items-center gap-3 border-0 border-b border-solid border-line-faint bg-white px-[14px] py-[13px] text-left"
    >
      <ToolIcon bg={iconBg}>{icon}</ToolIcon>
      <div className="flex-1 text-[14px] font-medium">{label}</div>
      <span className="text-[12.5px] text-slate-400">{detail}</span>
      <ChevronRight size={12} />
    </button>
  );
}

/** Change your own PIN (the one used at the front desk). Needs the current PIN. */
function ChangePinSheet({ onClose }: { onClose: () => void }) {
  const form = useForm<ChangePinInput>({
    resolver: zodResolver(changePinInput),
    defaultValues: { currentPin: '', newPin: '', confirmPin: '' },
  });
  const save = useMutation({
    mutationFn: (v: ChangePinInput) => ok(api.auth.pin.$put({ json: v })),
    onSuccess: () => {
      toast('PIN changed');
      onClose();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 400) {
        const field = (e.field as keyof ChangePinInput | undefined) ?? 'currentPin';
        form.setError(field, { message: e.message });
      } else form.setError('root', { message: e.message });
    },
  });
  const { errors } = form.formState;
  const pinProps = {
    type: 'password',
    inputMode: 'numeric',
    maxLength: 4,
    autoComplete: 'off',
    placeholder: '••••',
  } as const;
  return (
    <BottomSheet onClose={onClose}>
      <form onSubmit={form.handleSubmit((v) => save.mutate(v))} className="flex flex-col gap-3" noValidate>
        <div>
          <div className="font-display text-[20px] font-bold">Change PIN</div>
          <div className="mt-1 text-[13px] text-slate-500">
            Your 4-digit PIN for signing in at the front desk.
          </div>
        </div>
        <TextField
          label="Current PIN"
          compact
          {...pinProps}
          error={errors.currentPin?.message}
          {...form.register('currentPin')}
        />
        <div className="grid grid-cols-2 gap-[10px]">
          <TextField
            label="New PIN"
            compact
            {...pinProps}
            error={errors.newPin?.message}
            {...form.register('newPin')}
          />
          <TextField
            label="Repeat it"
            compact
            {...pinProps}
            error={errors.confirmPin?.message}
            {...form.register('confirmPin')}
          />
        </div>
        <FieldError>{errors.root?.message}</FieldError>
        <PrimaryButton type="submit" loading={save.isPending}>
          Save new PIN
        </PrimaryButton>
      </form>
    </BottomSheet>
  );
}

/** Make this device a front desk for the current center. */
function DeskOnSheet({ center, onClose }: { center: { id: string; name: string }; onClose: () => void }) {
  const qc = useQueryClient();
  const form = useForm<DeskCreateInput>({
    resolver: zodResolver(deskCreateInput),
    defaultValues: { centerId: center.id, name: 'Front desk' },
  });
  const create = useMutation({
    mutationFn: (v: DeskCreateInput) => ok<DeskCreatedDto>(api.desks.$post({ json: v })),
    onSuccess: ({ desk, token }) => {
      session.setDesk({
        id: desk.id,
        token,
        name: desk.name,
        centerId: desk.centerId,
        centerName: desk.centerName,
      });
      setBiometricEnabled(false); // a shared device: no personal Face ID unlock
      void qc.invalidateQueries({ queryKey: qk.desks });
      toast(`Front-desk mode on · ${desk.name}`);
      onClose();
    },
    onError: (e) => form.setError('root', { message: e.message }),
  });
  const { errors } = form.formState;
  return (
    <BottomSheet onClose={onClose}>
      <form onSubmit={form.handleSubmit((v) => create.mutate(v))} className="flex flex-col gap-3" noValidate>
        <div>
          <div className="font-display text-[20px] font-bold">Use this device as a front desk</div>
          <div className="mt-1.5 text-[13.5px] leading-normal text-slate-500">
            Staff at <b className="text-ink">{center.name}</b> will sign in here by tapping their name and
            entering their PIN. Only do this on a shared device that stays at the desk. You can turn it off
            from Team &amp; roles on any device.
          </div>
        </div>
        <TextField label="Device name" compact error={errors.name?.message} {...form.register('name')} />
        <FieldError>{errors.root?.message}</FieldError>
        <PrimaryButton type="submit" loading={create.isPending}>
          Turn on front-desk mode
        </PrimaryButton>
      </form>
    </BottomSheet>
  );
}

function DeskOffSheet({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const { desk } = useSession();
  const off = useMutation({
    mutationFn: () => ok<void>(api.desks[':id'].$delete({ param: { id: desk?.id ?? '' } })),
    onSettled: (_d, e) => {
      // already removed elsewhere (404) is fine too
      if (e && !(e instanceof ApiError && e.status === 404)) {
        toast(e.message);
        return;
      }
      session.setDesk(null);
      void qc.invalidateQueries({ queryKey: qk.desks });
      toast('Front-desk mode off');
      onClose();
    },
  });
  return (
    <BottomSheet onClose={onClose}>
      <div className="font-display text-[20px] font-bold">Turn off front-desk mode?</div>
      <div className="mt-2 text-[14px] leading-normal text-slate-500">
        Staff won't be able to pick their name here any more, and anyone who signed in on this device with
        their PIN is signed out.
      </div>
      <div className="mt-5 flex flex-col gap-[10px]">
        <PrimaryButton style={{ background: '#dc2626' }} loading={off.isPending} onClick={() => off.mutate()}>
          Turn off
        </PrimaryButton>
        <SecondaryButton onClick={onClose}>Keep it on</SecondaryButton>
      </div>
    </BottomSheet>
  );
}
